import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { memo, useMemo } from 'react';

import { palette, tokens } from '../theme';
import { usePending } from '../worklog';
import { DayCellEvents } from './DayCellEvents';
import { DayCellHeader } from './DayCellHeader';
import { computeAllocationFractions, computeDayDeltas, type DayCell } from './models';

export type DayCellViewProps = {
    cell: DayCell;
    isToday: boolean;
    isFuture: boolean;
    onClick: (cell: DayCell) => void;
};

const isCellClickable = (cell: DayCell, isFuture: boolean): boolean => {
    if (!cell.inMonth) {
        return false;
    }
    if (isFuture) {
        return false;
    }
    if (cell.nonWorking?.kind === 'weekend') {
        return false;
    }
    if (cell.nonWorking?.kind === 'holiday') {
        return false;
    }
    return true;
};

export const DayCellView = memo(function DayCellView({ cell, isToday, isFuture, onClick }: DayCellViewProps) {
    const classes = useStyles();
    const { forDate } = usePending();
    const pendingForDay = forDate(cell.date);

    const deltas = useMemo(() => computeDayDeltas(cell, pendingForDay), [cell, pendingForDay]);
    const { savedFrac, pendingFrac } = useMemo(
        () => computeAllocationFractions(cell.capacity, deltas.effectiveSavedHours, deltas.pendingAddHours),
        [cell.capacity, deltas.effectiveSavedHours, deltas.pendingAddHours],
    );

    const day = Number(cell.date.slice(8, 10));
    const clickable = isCellClickable(cell, isFuture);

    const hasAllocation = !cell.nonWorking && cell.inMonth && cell.capacity > 0 && deltas.totalHours > 0;
    const showProgress = !cell.nonWorking && cell.inMonth && cell.capacity > 0;

    const cellClasses = mergeClasses(
        classes.cell,
        cell.nonWorking?.kind === 'weekend' && classes.weekend,
        cell.nonWorking?.kind === 'holiday' && classes.holidayBg,
        cell.nonWorking?.kind === 'dayOff' && classes.dayOffBg,
        !cell.inMonth && classes.outsideMonth,
        cell.inMonth && isFuture && classes.future,
        clickable && classes.clickable,
    );

    const interactiveProps = clickable
        ? {
              role: 'button',
              tabIndex: 0,
              onClick: () => onClick(cell),
              onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onClick(cell);
                  }
              },
          }
        : {};

    return (
        <div className={cellClasses} {...interactiveProps}>
            <DayCellHeader
                day={day}
                isToday={isToday}
                isFuture={isFuture}
                showProgress={showProgress}
                hasAllocation={hasAllocation}
                totalHours={deltas.totalHours}
                capacity={cell.capacity}
                savedFrac={savedFrac}
                pendingFrac={pendingFrac}
            />

            {cell.nonWorking?.kind === 'holiday' && (
                <div className={mergeClasses(classes.pill, classes.holidayPill)}>
                    <div className={classes.pillRow}>
                        <span className={mergeClasses(classes.pillDot, classes.holidayDot)} />
                        <span className={classes.pillText}>{cell.nonWorking.name}</span>
                    </div>
                </div>
            )}

            {cell.nonWorking?.kind === 'dayOff' && (
                <div className={mergeClasses(classes.pill, classes.dayOffPill)}>
                    <div className={classes.pillRow}>
                        <span className={mergeClasses(classes.pillDot, classes.dayOffDot)} />
                        <span className={classes.pillText}>Day off</span>
                    </div>
                </div>
            )}

            <DayCellEvents savedEntries={cell.savedEntries} pendingAdds={deltas.pendingAdds} removalByKey={deltas.removalByKey} />
        </div>
    );
});

const useStyles = makeStyles({
    cell: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minHeight: 0,
        boxSizing: 'border-box',
        padding: '8px 8px 6px',
        borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        outline: 'none',
        overflow: 'hidden',
    },
    clickable: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: palette.hoverTint,
        },
        '&:focus-visible': {
            backgroundColor: palette.focusTint,
            outline: `2px solid ${tokens.colorNeutralStroke1}`,
            outlineOffset: '-2px',
        },
    },
    weekend: {
        backgroundColor: palette.nonWorkingBg,
    },
    holidayBg: {
        backgroundColor: palette.nonWorkingBg,
    },
    dayOffBg: {
        backgroundColor: palette.nonWorkingBg,
    },
    outsideMonth: {
        opacity: 0.4,
    },
    future: {
        backgroundImage: `repeating-linear-gradient(135deg, transparent 0 6px, ${palette.cardBorder} 6px 7px)`,
        cursor: 'not-allowed',
    },
    pill: {
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
        padding: '8px 10px',
        borderRadius: '8px',
        marginTop: '2px',
        marginBottom: '2px',
        flexGrow: 1,
        flexShrink: 0,
        boxSizing: 'border-box',
    },
    pillRow: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        minWidth: 0,
    },
    holidayPill: {
        backgroundColor: palette.holidayBg,
    },
    dayOffPill: {
        backgroundColor: palette.dayOffBg,
    },
    pillDot: {
        width: '5px',
        height: '5px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    holidayDot: {
        backgroundColor: palette.holidayAccent,
    },
    dayOffDot: {
        backgroundColor: palette.dayOffAccent,
    },
    pillText: {
        fontSize: '10.5px',
        fontWeight: tokens.fontWeightSemibold,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
});
